import type { UseNavigationDropdownProps } from '@maestro/commerce-blocks/components/navigation-header/navigation-desktop-dropdown.types'

import { KeyboardEvent, useCallback } from 'react'

import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { EventType } from '@maestro/data-layer'

export const useNavigationDropdown = (props: UseNavigationDropdownProps) => {
  const { title, closeDropdown, isActive } = props
  const { publish } = useEventBusContext()

  const handleClickMenu = useCallback(
    (level2Title?: string, level3Title?: string) => {
      publish(EventType.MENU_CLICKED, {
        level1: title || undefined,
        level2: level2Title || undefined,
        level3: level3Title || undefined,
      })
      closeDropdown()
    },
    [closeDropdown, publish, title]
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isActive) {
        e.preventDefault()
        e.stopPropagation()
        closeDropdown()
      }
    },
    [closeDropdown, isActive]
  )

  const secondLevelSubItemKeyDown = useCallback((keyEvent: KeyboardEvent) => {
    switch (keyEvent.key) {
      case 'ArrowDown': {
        keyEvent.preventDefault()
        const nextSubItem = keyEvent.currentTarget.parentElement?.nextSibling
          ?.firstChild as HTMLElement
        nextSubItem?.focus()
        keyEvent.stopPropagation()
        break
      }
      case 'ArrowUp': {
        keyEvent.preventDefault()
        let prevSubitem = keyEvent.currentTarget.parentElement?.previousSibling
          ?.firstChild as HTMLElement
        if (prevSubitem && prevSubitem.className === 'nav-desktop__subtitle') {
          const lastChild = prevSubitem.parentElement?.lastChild?.lastChild
            ?.firstChild as HTMLElement
          if (lastChild) {
            prevSubitem = lastChild
          }
        }
        prevSubitem?.focus()
        keyEvent.stopPropagation()
        break
      }
      case 'ArrowRight': {
        keyEvent.preventDefault()
        const nextSibling = keyEvent.currentTarget.parentElement
          ?.nextSibling as HTMLElement
        const nextSubitem = nextSibling?.querySelector('a')
        nextSubitem?.focus()
        keyEvent.stopPropagation()
        break
      }
      case 'ArrowLeft': {
        keyEvent.preventDefault()
        const currentElement = keyEvent.currentTarget as HTMLElement
        let prevSecondLevelElement = currentElement.parentElement
          ?.previousSibling as HTMLElement

        if (!prevSecondLevelElement) {
          prevSecondLevelElement = currentElement.parentElement?.parentElement
            ?.lastChild as HTMLElement
        }

        const prevThirdLevelElements = prevSecondLevelElement?.querySelectorAll(
          '.nav-desktop__subnav > li > a'
        )
        const lastElementToFocus =
          prevThirdLevelElements && prevThirdLevelElements.length > 0
            ? (prevThirdLevelElements[
                prevThirdLevelElements.length - 1
              ] as HTMLElement)
            : (prevSecondLevelElement?.querySelector(
                '.nav-desktop__sublink'
              ) as HTMLElement)

        lastElementToFocus?.focus()
        keyEvent.stopPropagation()
        break
      }
    }
  }, [])

  const secondLevelWithChildrenSubitemKeyDown = useCallback(
    (keyEvent: KeyboardEvent) => {
      switch (keyEvent.key) {
        case 'ArrowDown': {
          keyEvent.preventDefault()
          const listElement = keyEvent.currentTarget?.parentElement
            ?.parentElement as HTMLElement
          const nextLink = listElement?.querySelector('ul')?.firstChild
            ?.firstChild as HTMLElement
          nextLink?.focus()
          keyEvent.stopPropagation()
          break
        }
        case 'ArrowUp': {
          keyEvent.preventDefault()
          let prevSubitem = keyEvent.currentTarget.parentElement?.parentElement
            ?.previousSibling?.firstChild as HTMLElement
          if (
            prevSubitem &&
            prevSubitem.className === 'nav-desktop__subtitle'
          ) {
            const lastChild = prevSubitem.parentElement?.lastChild?.lastChild
              ?.firstChild as HTMLElement
            if (lastChild) {
              prevSubitem = lastChild
            }
          }
          prevSubitem?.focus()
          keyEvent.stopPropagation()
          break
        }
        case 'ArrowRight': {
          keyEvent.preventDefault()
          const nextSibling = keyEvent.currentTarget.parentElement
            ?.parentElement?.nextSibling as HTMLElement
          const nextSubitem = nextSibling?.querySelector('a')
          nextSubitem?.focus()
          keyEvent.stopPropagation()
          break
        }
        case 'ArrowLeft': {
          keyEvent.preventDefault()
          const currentElement2 = keyEvent.currentTarget as HTMLElement
          let prevSiblingElement = currentElement2.closest(
            '.nav-desktop__subitem'
          )?.previousSibling as HTMLElement

          if (!prevSiblingElement) {
            prevSiblingElement = currentElement2.closest('.nav-desktop__subnav')
              ?.parentElement?.lastChild as HTMLElement
          }

          const lastLinkToFocus = prevSiblingElement?.querySelector(
            '.nav-desktop__sublink'
          ) as HTMLElement
          lastLinkToFocus?.focus()
          keyEvent.stopPropagation()
          break
        }
      }
    },
    []
  )

  const thirdLevelSubitemKeyDown = useCallback((keyEvent: KeyboardEvent) => {
    switch (keyEvent.key) {
      case 'ArrowDown': {
        keyEvent.preventDefault()
        const currentElement = keyEvent.currentTarget as HTMLElement
        let nextSubitem = currentElement.parentElement?.nextSibling
          ?.firstChild as HTMLElement

        if (!nextSubitem) {
          const nextSibling = currentElement.closest('.nav-desktop__subnav')
            ?.parentElement?.nextSibling as HTMLElement
          nextSubitem = nextSibling?.querySelector(
            '.nav-desktop__sublink'
          ) as HTMLElement
        }

        nextSubitem?.focus()
        keyEvent.stopPropagation()
        break
      }
      case 'ArrowUp': {
        keyEvent.preventDefault()
        let prevSubItem = keyEvent.currentTarget.parentElement?.previousSibling
          ?.firstChild as HTMLElement
        if (!prevSubItem) {
          const parentHeader = keyEvent.currentTarget.parentElement?.closest(
            '.nav-desktop__subitem'
          ) as HTMLElement
          prevSubItem = parentHeader?.firstChild as HTMLElement
        }
        prevSubItem?.focus()
        keyEvent.stopPropagation()
        break
      }
      case 'ArrowRight': {
        keyEvent.preventDefault()
        const eventElement = keyEvent.currentTarget as HTMLElement
        const currentSubnav = eventElement.closest('.nav-desktop__subnav')
        const currentSubItemIndex = Array.from(
          currentSubnav?.children || []
        ).indexOf(eventElement.parentElement as HTMLLIElement)

        let nextSecondLevelElement = currentSubnav?.parentElement
          ?.nextSibling as HTMLElement
        if (!nextSecondLevelElement) {
          nextSecondLevelElement = currentSubnav?.parentElement?.parentElement
            ?.firstChild as HTMLElement
        }

        const nextThirdLevelElements = nextSecondLevelElement?.querySelectorAll(
          '.nav-desktop__subnav > li > a'
        )
        const nextElementToFocus =
          nextThirdLevelElements && nextThirdLevelElements[currentSubItemIndex]
            ? (nextThirdLevelElements[currentSubItemIndex] as HTMLElement)
            : (nextSecondLevelElement?.querySelector(
                '.nav-desktop__sublink'
              ) as HTMLElement)

        nextElementToFocus?.focus()
        keyEvent.stopPropagation()
        break
      }
      case 'ArrowLeft': {
        keyEvent.preventDefault()
        const eventElement2 = keyEvent.currentTarget as HTMLElement
        const currentSubnav2 = eventElement2.closest('.nav-desktop__subnav')
        const currentSubItemIndex2 = Array.from(
          currentSubnav2?.children || []
        ).indexOf(eventElement2.parentElement as HTMLElement)

        let previousSecondLevelElement = currentSubnav2?.parentElement
          ?.previousSibling as HTMLElement
        if (!previousSecondLevelElement) {
          previousSecondLevelElement = currentSubnav2?.parentElement
            ?.parentElement?.lastChild as HTMLElement
        }

        const previousThirdLevelElements =
          previousSecondLevelElement?.querySelectorAll(
            '.nav-desktop__subnav > li > a'
          )
        const previousElementToFocus =
          previousThirdLevelElements &&
          previousThirdLevelElements[currentSubItemIndex2]
            ? (previousThirdLevelElements[currentSubItemIndex2] as HTMLElement)
            : (previousSecondLevelElement?.querySelector(
                '.nav-desktop__sublink'
              ) as HTMLElement)

        previousElementToFocus?.focus()
        keyEvent.stopPropagation()
        break
      }
    }
  }, [])

  return {
    handleClickMenu,
    handleKeyDown,
    secondLevelSubItemKeyDown,
    secondLevelWithChildrenSubitemKeyDown,
    thirdLevelSubitemKeyDown,
  }
}
